import React, { useMemo } from 'react';
import { TemplateElement } from '@bynder-studio/render-core';
import { List, InlineBanner } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { flattenTree } from '~/common/editor/helpers/elementtree';
import ShotItemGlobal from 'packages/pages/design/sidebar/shots/ShotItemGlobal';
import { useReviewStatus } from 'packages/pages/design/sidebar/shots/useReviewStatus';
import useVariations from 'packages/pages/design/hooks/useVariations';
import useDesign from 'packages/pages/design/hooks/useDesign';
import ShotReviewBanner from 'packages/pages/design/sidebar/shots/ShotReviewBanner';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';
import SceneRows from './SceneRows';
import { getGlobalEditableElements, getImageDesignSceneRows, getSceneElementStateMap } from './utils';

type Props = {
    elements: TemplateElement[];
    isToolbarDisabled: boolean;
    isLoading: boolean;
};

const ShotImageDesign = ({ elements, isToolbarDisabled, isLoading }: Props) => {
    const { translate } = useTranslate();
    const { isDisabledByReview } = useReviewStatus();
    const { deletedAssets } = useVariations();
    const { creativeModel } = useDesign();
    const { isPlatformAllowed } = useAccessRights();
    const currentPageIndex = creativeModel.getCurrentPageIndex();
    const currentModel = creativeModel.getModels()[currentPageIndex];

    const isDynamicElementSelectionEnabled = useMemo(
        () => isPlatformAllowed([features.DYNAMIC_ELEMENT_SELECTION]),
        [isPlatformAllowed],
    );

    const elementStatesById = getSceneElementStateMap(currentModel?.getAllElementsRecursively?.() || []);

    const sceneRows = getImageDesignSceneRows({
        elements,
        elementStatesById,
        isDynamicElementSelectionEnabled,
    });
    const globalEditableElements = useMemo(
        () => getGlobalEditableElements(flattenTree(elements) as TemplateElement),
        [elements],
    );

    return (
        <List>
            <ShotReviewBanner />
            {!!deletedAssets.length && (
                <InlineBanner variant="error">{translate('design.sidebar.scene.replace-missing-asset')}</InlineBanner>
            )}
            {globalEditableElements.map((element, idx) => (
                <ShotItemGlobal
                    key={element.id}
                    id={element.id}
                    isDisabled={isDisabledByReview || isToolbarDisabled}
                    isLast={idx === globalEditableElements.length - 1}
                />
            ))}
            <SceneRows
                rows={sceneRows}
                isToolbarDisabled={isToolbarDisabled}
                isLoading={isLoading}
                isDisabledByReview={isDisabledByReview}
            />
        </List>
    );
};

export default ShotImageDesign;
